import { Avatar } from 'components/Avatar';
import { Button, ButtonTheme } from 'components/Button';
import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { FileAddButtons } from 'components/Field/FileAddButtons';
import { TextField } from 'components/Field/TextField';
import { Icon, ICONS } from 'components/Icon';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { LocalThumbnail } from 'components/Thumbnail/LocalThumbnail';
import { useAlert } from 'hooks/useAlert';
import { useConfirm } from 'hooks/useConfirm';
import { useSelector } from 'hooks/useSelector';
import { filterArrayEmpty, filterArrayNullUndefined } from 'lib/riskbase';
import { theme } from 'lib/theme';
import * as React from 'react';
import { View } from 'react-native';
import { useDispatch } from 'react-redux';
import { API_ASE_IMAGE } from 'service/api/constants';
import { FileTypeExtensions } from 'service/file/types';
import { doFilePreview } from 'store/actions';
import { selectUrlToken } from 'store/auth/selectors';
import {
  AppContext,
  AppContextEnum,
  Nullable,
  appContextToFileContext,
} from 'types/types';

type Details = {
  name: string;
  avatar: string;
  reference: string;
  customReference: string;
  parentName: string;
  address1: string;
  address2: string;
  city: string;
  county: string;
  postcode: string;
  phone: string;
  organisationName?: string;
  teams?: {
    id: string;
    name: string;
    people: string;
  }[];
};

type DetailsNew = Partial<
  Nullable<
    Omit<Details, 'parentName' | 'organisationName' | 'reference' | 'teams'>
  >
>;

type Props = {
  context: AppContext;
  editable: {
    avatar: boolean;
    name: boolean;
    customReference: boolean;
    address: boolean;
  };
  title?: string;
  onChanges: (data: Partial<Record<keyof DetailsNew, string | null>>) => void;
  onPressBackButton?: (() => void) | undefined;
  details: Details;
  detailsNew?: DetailsNew;
  onPressSaveButton?: () => void;
  onPressCancelButton?: () => void;
  onEsc?: () => void;
  saveButtonText?: string;
  isModal: boolean;
};

export const AssetSummaryPage: React.FC<Props> = ({
  context,
  editable,
  title,
  onChanges,
  onPressBackButton,
  onPressSaveButton,
  onPressCancelButton,
  onEsc,
  details,
  detailsNew,
  saveButtonText,
  isModal,
}) => {
  const dispatch = useDispatch();
  const alert = useAlert();
  const confirm = useConfirm();
  const lastRemovedKey = React.useRef(Math.random());
  const urlToken = useSelector(selectUrlToken);

  const handleChange = (key: keyof DetailsNew, value: string | null) =>
    onChanges({ [key]: value });

  const handleRevert = (
    data: Partial<Record<keyof DetailsNew, string | null>>,
  ) =>
    confirm({
      text: "This field has been changed in this assessment. Continuing will revert the value back to the asset's value",
      confirmText: 'Revert',
      destructive: true,
      onConfirm: () => {
        lastRemovedKey.current = Math.random();
        onChanges(data);
      },
    });

  const handleRemoveChange = (key: keyof DetailsNew) =>
    handleRevert({ [key]: null });

  const handleRemoveChangeAddress = () =>
    handleRevert({
      address1: null,
      address2: null,
      city: null,
      county: null,
      postcode: null,
    });

  const getIsModified = (key: keyof DetailsNew): boolean => {
    const detailNew = detailsNew?.[key];
    return detailNew !== undefined && detailNew !== null;
  };

  const getMerged = (key: keyof DetailsNew): string => {
    const detailNew = detailsNew?.[key];
    return detailNew !== undefined && detailNew !== null
      ? detailNew
      : details[key] || '';
  };

  const getExplanationText = (
    key: keyof DetailsNew,
    forAccessibility: boolean = false,
  ): string | undefined =>
    getIsModified(key)
      ? forAccessibility
        ? `Reverts ${key} back to ${details[key] ? details[key] : 'No Value'}`
        : `${details[key] ? details[key] : 'No Value'}`
      : undefined;

  const getExplanationTextForAddress = (
    forAccessibility: boolean = false,
  ): string | undefined => {
    const isModified =
      getIsModified('address1') ||
      getIsModified('address2') ||
      getIsModified('city') ||
      getIsModified('county') ||
      getIsModified('postcode');
    if (isModified) {
      return (
        (forAccessibility ? 'Reverts address back to ' : '') +
        ([
          details.parentName,
          details.address1,
          details.address2,
          details.city,
          details.county,
          details.postcode,
        ]
          .filter(filterArrayEmpty)
          .join(', ') || 'No Value')
      );
    }
    return undefined;
  };

  const undoImageText =
    context.context !== AppContextEnum.localAsset
      ? `The asset's image will be ${
          detailsNew?.avatar ? 'changed' : 'removed'
        } when this assessment is approved. Undo this to leave the asset's image untouched`
      : "The asset's image has been changed";

  const isAssessmentDraft =
    context.context !== AppContextEnum.localAsset &&
    (editable.address ||
      editable.avatar ||
      editable.customReference ||
      editable.name ||
      !!Object.values(detailsNew || {}).filter(filterArrayNullUndefined)
        .length);

  return (
    <PageScrollView
      isInModal={isModal}
      contentPaddingTop={0}
      header={
        <NavigationHeader
          heading={title || 'Overview'}
          onPressBackButton={onPressBackButton}
          onPressSaveButton={onPressSaveButton}
          onPressCancelButton={onPressCancelButton}
          saveButtonText={saveButtonText}
          subheading={isAssessmentDraft ? 'Draft' : undefined}
          subheadingIcon={isAssessmentDraft ? ICONS.CircleInfoLight : undefined}
          onPressSubheading={
            isAssessmentDraft
              ? () =>
                  alert({
                    text:
                      'Changes will be applied to the asset when the assessment is approved\n\n' +
                      'Until then, the asset will continue to appear in RiskBase with its existing details\n\n' +
                      'Existing values are shown below changes',
                  })
              : undefined
          }
        />
      }
    >
      <View
        style={{
          alignItems: 'center',
          flex: 1,
          flexDirection: 'column',
          paddingHorizontal: theme.paddingHorizontal,
          paddingBottom: theme.paddingVertical,
          paddingTop: theme.paddingVertical,
        }}
      >
        <View
          style={{
            borderRadius: 200 / 2,
          }}
        >
          {context.context === AppContextEnum.remoteAssessment ? (
            <Avatar
              size={200}
              data={
                !!getMerged('avatar')
                  ? {
                      remoteUrl: API_ASE_IMAGE(context.assessmentId, {
                        urlToken,
                      })(getMerged('avatar')),
                    }
                  : null
              }
            />
          ) : (
            <LocalThumbnail
              thumbnailId={getMerged('avatar')}
              width={200}
              height={200}
              rounded
              onPress={() =>
                dispatch(
                  doFilePreview({
                    fileId: getMerged('avatar'),
                    typeId: FileTypeExtensions.jpeg,
                    context: appContextToFileContext(context),
                  }),
                )
              }
            />
          )}
        </View>
        {editable.avatar ? (
          <View
            style={{
              alignItems: 'center',
              flexDirection: 'row',
              marginTop: -theme.grid * 2,
            }}
          >
            <FileAddButtons
              context={appContextToFileContext(context)}
              type="images"
              promptMessage="Set Asset Image"
              removeText="Remove Asset Image"
              showAddLabel={false}
              onChange={(fileIds) =>
                fileIds.length ? handleChange('avatar', fileIds[0]!) : undefined
              }
              onRemove={
                getMerged('avatar')
                  ? () =>
                      confirm({
                        confirmText: 'Remove Image',
                        destructive: true,
                        onConfirm: () => handleChange('avatar', ''),
                        text:
                          'This asset will no longer have an image\n\n' +
                          "The image will remain available in the asset's files",
                      })
                  : undefined
              }
              shadow
            />
            {getIsModified('avatar') && (
              <>
                <View
                  style={{
                    marginLeft: theme.paddingHorizontal,
                    marginRight: theme.paddingHorizontal / 2,
                    backgroundColor: 'white',
                    borderRadius: 4,
                  }}
                >
                  <Button
                    icon={ICONS.ClockRotateLeftLight}
                    buttonTheme={ButtonTheme.secondary}
                    onPress={() =>
                      confirm({
                        title: 'Undo Change',
                        text: undoImageText,
                        confirmText: 'Undo',
                        onConfirm: () => handleRemoveChange('avatar'),
                      })
                    }
                  />
                </View>
                <LocalThumbnail
                  thumbnailId={details.avatar || undefined}
                  width={30}
                  height={30}
                  rounded
                  accessibilityLabel={
                    context.context !== AppContextEnum.localAsset
                      ? 'Current asset image outside of assessment'
                      : 'Previous asset image'
                  }
                  accessibilityHint={
                    details.avatar
                      ? `Preview this asset's ${
                          context.context !== AppContextEnum.localAsset
                            ? 'current'
                            : 'previous'
                        } image`
                      : undefined
                  }
                />
              </>
            )}
          </View>
        ) : (
          getIsModified('avatar') && (
            <View
              style={{
                alignItems: 'center',
                flexDirection: 'row',
                marginTop: -theme.grid * 2,
                backgroundColor: 'white',
                borderRadius: 15,
                padding: 1,
              }}
            >
              <Icon
                source={ICONS.ClockRotateLeftLight}
                style={{ marginHorizontal: theme.paddingHorizontal / 2 }}
              />
              {details.avatar ? (
                <LocalThumbnail
                  thumbnailId={details.avatar || undefined}
                  width={30}
                  height={30}
                  rounded
                  accessibilityLabel={
                    context.context !== AppContextEnum.localAsset
                      ? 'Current asset image outside of assessment'
                      : 'Previous asset image'
                  }
                  accessibilityHint={
                    details.avatar
                      ? `Preview this asset's ${
                          context.context !== AppContextEnum.localAsset
                            ? 'current'
                            : 'previous'
                        } image`
                      : undefined
                  }
                />
              ) : (
                <Avatar data={null} size={30} iconPadding={5} />
              )}
            </View>
          )
        )}
      </View>

      {editable.name ? (
        <Card
          explanationLinkText={getExplanationText('name')}
          explanationLinkAccessibilityHint={getExplanationText('name', true)}
          explanationLinkIcon={ICONS.ClockRotateLeftLight}
          onExplanationLinkPress={() => handleRemoveChange('name')}
        >
          <TextField
            name="name"
            value={getMerged('name')}
            placeholder="Name"
            key={lastRemovedKey.current}
            onChangeWithName={handleChange}
            autoCapitalize="words"
            onEsc={onEsc}
            autoFocusWeb
            autoFocusDelay
          />
        </Card>
      ) : (
        <Card
          explanation={getExplanationText('name')}
          explanationIcon={ICONS.ClockRotateLeftLight}
        >
          <CardRow value={getMerged('name')} />
        </Card>
      )}

      {editable.address ? (
        !details.parentName ? (
          <Card
            label="Address"
            explanationLinkText={getExplanationTextForAddress()}
            explanationLinkAccessibilityHint={getExplanationTextForAddress(
              true,
            )}
            explanationLinkIcon={ICONS.ClockRotateLeftLight}
            onExplanationLinkPress={handleRemoveChangeAddress}
          >
            <View>
              <TextField
                name="address1"
                value={getMerged('address1')}
                placeholder="Line 1"
                autoComplete="addressLine1"
                key={lastRemovedKey.current}
                onChangeWithName={handleChange}
                autoCapitalize="words"
                onEsc={onEsc}
              />
            </View>
            <TextField
              name="address2"
              value={getMerged('address2')}
              placeholder="Line 2"
              autoComplete="addressLine2"
              key={lastRemovedKey.current}
              onChangeWithName={handleChange}
              autoCapitalize="words"
              onEsc={onEsc}
            />
            <TextField
              name="city"
              value={getMerged('city')}
              placeholder="City"
              autoComplete="addressCity"
              key={lastRemovedKey.current}
              onChangeWithName={handleChange}
              autoCapitalize="words"
              onEsc={onEsc}
            />
            <TextField
              name="county"
              value={getMerged('county')}
              placeholder="County"
              autoComplete="addressCounty"
              key={lastRemovedKey.current}
              onChangeWithName={handleChange}
              autoCapitalize="words"
              onEsc={onEsc}
            />
            <TextField
              name="postcode"
              value={getMerged('postcode')}
              placeholder="Postcode"
              autoComplete="addressPostcode"
              key={lastRemovedKey.current}
              onChangeWithName={handleChange}
              autoCapitalize="characters"
              onEsc={onEsc}
            />
          </Card>
        ) : (
          <Card
            label="Address"
            explanation="This address is inherited from a parent asset and cannot be modified"
          >
            <CardRow
              value={[
                details.parentName,
                getMerged('address1'),
                getMerged('address2'),
                getMerged('city'),
                getMerged('county'),
                getMerged('postcode'),
              ]
                .filter(filterArrayEmpty)
                .join('\n')}
            />
          </Card>
        )
      ) : (
        <Card
          label="Address"
          explanation={getExplanationTextForAddress()}
          explanationIcon={ICONS.ClockRotateLeftLight}
        >
          <CardRow
            value={
              [
                details.parentName,
                getMerged('address1'),
                getMerged('address2'),
                getMerged('city'),
                getMerged('county'),
                getMerged('postcode'),
              ]
                .filter(filterArrayEmpty)
                .join('\n') || 'No Address'
            }
            placeholder="No Value"
          />
        </Card>
      )}

      {editable.address ? (
        <Card
          label="Phone"
          explanationLinkText={getExplanationText('phone')}
          explanationLinkAccessibilityHint={getExplanationText('phone', true)}
          explanationLinkIcon={ICONS.ClockRotateLeftLight}
          onExplanationLinkPress={() => handleRemoveChange('phone')}
        >
          <TextField
            name="phone"
            value={getMerged('phone')}
            autoComplete="phone"
            key={lastRemovedKey.current}
            onChangeWithName={handleChange}
            onEsc={onEsc}
          />
        </Card>
      ) : (
        <Card
          label="Phone"
          explanation={getExplanationText('phone')}
          explanationIcon={ICONS.ClockRotateLeftLight}
        >
          <CardRow value={getMerged('phone')} placeholder="No Value" />
        </Card>
      )}

      {editable.customReference ? (
        <Card
          label="Custom Reference"
          explanationLinkText={getExplanationText('customReference')}
          explanationLinkAccessibilityHint={getExplanationText(
            'customReference',
            true,
          )}
          explanationLinkIcon={ICONS.ClockRotateLeftLight}
          onExplanationLinkPress={() => handleRemoveChange('customReference')}
        >
          <TextField
            name="customReference"
            value={getMerged('customReference')}
            key={lastRemovedKey.current}
            onChangeWithName={handleChange}
            onEsc={onEsc}
          />
        </Card>
      ) : (
        <Card
          label="Custom Reference"
          explanation={getExplanationText('customReference')}
          explanationIcon={ICONS.ClockRotateLeftLight}
        >
          <CardRow
            value={getMerged('customReference')}
            placeholder="No Value"
          />
        </Card>
      )}

      {!!details.teams?.length && (
        <Card label="People Responsible for Teams">
          {details.teams.map((team) => (
            <CardRow
              labelAlignTop
              key={team.id}
              label={team.name}
              value={team.people}
            />
          ))}
        </Card>
      )}

      {!!details.organisationName && (
        <Card label="Organisation">
          <CardRow labelAlignTop value={details.organisationName} />
        </Card>
      )}
    </PageScrollView>
  );
};
